<template>
  <div>

    <section class="experience d-flex align-items-center">
      <div class="container py-5">
      </div>
    </section>

    <section class="py-4">
      <div class="container">
        <div class="p-4">
          <h5 class="fw-bold mb-2"><span style="color:var(--brand)">EXPERIENCE</span></h5>
          <div class="d-flex justify-content-start" style="padding-top: 20px;">
            <p class="mb-0">
              Mr. Li began his career with SPTDI (Shanghai Postal and Telecommunication Design Institute) 
              first as a junior structural engineer, then as an engineer upon receipt of his degree from TongJi University in 1992. 
              Through his eight years as a structural engineer, he increased his engineering knowledge as well as the understanding of architectural, building mechanical/electrical system design and planning concept.
            </p>
          </div>                                                               
          <div class="d-flex justify-content-start" style="padding-top: 16px;">
            <p class="mb-0">
              Mr. Li moved to Calgary in 2000, and joined KellamBerg Engineering & Surveyors Ltd. 
              where he continued his career from May 2001 to July, 2007, He had the opportunity working at variety positions including: 
              draftsperson, field inspector, design engineer, project engineer and manager. From 2004 to 2007, 
              he was responsible for design, city approval, coordination and client liaison.
            </p>                    
          </div>
          <div class="d-flex justify-content-start" style="padding-top: 16px;">
            <p class="mb-0">
              {{ COMPANYFULLNAME }} has completed or is currently undertaking the design of following projects in Alberta, British Columbia, Saskatchewan, Ontario and Manitoba.
            </p>                    
          </div>                
          
          <div class="d-flex justify-content-start" style="padding-top: 20px; width: 100%;">
            <el-collapse
              v-model="activeName"
              accordion
              class="experience-collapse"
              :props="{ 'class': 'no-border' }"
              style="width: 100%;"
            >
              <el-collapse-item
                v-for="cat in nonEmptyCategories"
                :key="cat.catalogId"
                :name="cat.catalogId"
              >
                <template #title>
                  <div class="collapse-title d-flex justify-content-between align-items-center w-100">
                    <div><span class="cat-title">{{ cat.experienceCatalog }}</span></div>
                    <div><span class="cat-meta">{{ cat.expProjectListInfos.length }} projects</span></div>
                  </div>
                </template>

                <div class="projects-grid d-grid gap-1 mt-1" style="grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));">
                  <div
                    v-for="proj in cat.expProjectListInfos"
                    :key="proj.projectId"
                    class="project-card"
                    role="link"
                    tabindex="0"
                    @click="goToProject(proj.projectId)"
                    @keydown.enter.prevent="goToProject(proj.projectId)"
                  >
                    <div class="thumb-wrap d-flex justify-content-start">
                      <img
                        :src="getThumbSrc(proj)"
                        :alt="proj.title"
                        class="thumb"
                        @error="onThumbError"
                      />
                      <div class="project-title" style="padding-left: 6px;">{{ proj.title }}</div>
                    </div>
                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>

          <div class="d-flex justify-content-start" style="padding-top: 20px;">
            <p class="mb-0">
              Mr. Robin Li also completed the multi family and commercial sites in Airdrie and Fort McMurray.
            </p>                    
          </div>
        </div>             
      </div>
    </section>

  </div>
</template>

<script setup>
import {COMPANYFULLNAME, TIPSTAG_ERROR} from '@/constants';
import { ref, getCurrentInstance, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/apiClient';
import defaultThumb from '@/assets/defaultError.png';

const { proxy } = getCurrentInstance();

const router = useRouter();
const categories = ref([]);
const activeName = ref([]);

const nonEmptyCategories = computed(() => {
  return (categories.value || []).filter(c => Array.isArray(c.expProjectListInfos) && c.expProjectListInfos.length > 0);
});



function getThumbSrc(proj) {
  if (!proj) return defaultThumb;
    
  if (proj.projectId) {
    if(proj.isVideo) {
      return `/projects/thumbs/${proj.projectId}.png`;
  } else {
      return `/projects/${proj.projectId}.png`;      
    }
  }

  return defaultThumb;
}

// handle thumb load error -> replace with default
function onThumbError(e) {
  if (!e?.target) return;
  e.target.onerror = null;
  e.target.src = defaultThumb;
}

function goToProject(projectId) {
  // Navigate to project detail page. Ensure route 'ProjectDetail' exists in your router.
  router.push({ name: 'ExperienceDetail', params: { projectId: projectId, isLogin: false } });
}


async function loadExperienceListData() {
  try {
    const response = await apiClient.post('/Main/getExpList');
    if(response && response.data) {
      categories.value = response.data.expList || [];      
      activeName.value = categories.value.length > 0 ? [categories.value[0].catalogId] : [];
    }
  } catch (e) {
    if(e.response && e.response.data && e.response.status === 400) {    
      proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
    } else {
      proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
    } 
  }
}

onMounted(() => { loadExperienceListData(); });

</script>

<style scoped>
/* Overall look: modern, dark-friendly, integrate with existing site theme */
.brand { color: var(--brand); }

/* Collapse container */
.experience-collapse {
  max-width: 1100px;
  margin: 12px auto 0;
  background: rgba(6,18,28,0.60);    /* deep dark surface */
  border-radius: 12px;
  padding: 8px;
  border: 1px solid rgba(255,255,255,0.03);
}

/* scope Element Plus internals using deep selector so styles apply to child components */
::v-deep .el-collapse {
  background: transparent;
  border-radius: 10px;
}

/* header (title row) — darker, flush with site, bright-ish text */
::v-deep .el-collapse-item__header {
  background: rgba(6,18,28,0.00);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 8px;
  color: rgba(255,255,255,0.92);     /* 灰白亮色 */
  font-weight: 700;
  transition: background .12s ease, color .12s ease;
}

/* header hover / focus */
::v-deep .el-collapse-item__header:hover {
  background: rgba(255,255,255,0.02);
}

/* arrow (chevron) tone */
::v-deep .el-collapse-item__header .el-icon {
  color: rgba(255,255,255,0.72);
}

/* expanded panel content area */
::v-deep .el-collapse-item__wrap {
  background: rgba(6,18,28,0.50);    /* slightly lighter panel surface */
  margin-top: 10px;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.03);
}


/* make project cards blend with panel */
.project-card {
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.03);
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
}
.project-card:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(13,110,253,0.12);
}
.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(2,6,23,0.6);
  background: rgba(255,255,255,0.03);
}

/* titles / metadata colors */
.cat-title { color: rgba(255,255,255,0.94); }
.cat-meta  { color: rgba(255,255,255,0.60); }

/* make category title larger and slightly brighter */
.cat-title {
  font-size: 1rem;               /* larger title */
  font-weight: 800;
  color: rgba(255,255,255,0.98);
  letter-spacing: 0.2px;
}

/* make project-count bigger and more visible */
.cat-meta {
  font-size: 0.90rem;               /* slightly larger than before */
  font-weight: 600;
  color: rgba(255,255,255,0.82);    /* brighter tone */
}

/* ensure header container doesn't downscale text */
::v-deep .el-collapse-item__header {
  gap: .6rem;
  font-size: inherit;
}

/* thumbnail border subtle */
.thumb {
  border: 1px solid rgba(255,255,255,0.04);
  box-shadow: 0 6px 18px rgba(2,6,23,0.55);
}

/* compact & consistent spacing on small screens */
@media (max-width: 767.98px) {
  .experience-collapse { padding: 6px; }
  ::v-deep .el-collapse-item__header { padding: 10px 12px; font-size: .98rem; }
  .projects-grid { gap: 10px; }
}

/* Responsive tweaks */
@media (max-width: 420px) {
  .projects-grid {
    grid-template-columns: 1fr;
  }
  .thumb-wrap { width: 86px; height: 64px; }
}

/* ensure header title area aligns items vertically center */
.collapse-title {
  display: flex;
  align-items: center; /* vertical center */
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  min-height: 48px; /* give header a consistent height */
}

/* category title: vertically centered and bright white */
.cat-title {
  display: flex;
  align-items: center; /* vertical center within its container */
  height: 100%;
  font-size: 1.00rem;
  font-weight: 800;
  color: #ffffff;            /* bright white */
  letter-spacing: 0.2px;
  margin: 0;
}

/* project-count: slightly smaller but brighter than before */
.cat-meta {
  display: flex;
  align-items: center;
  height: 100%;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255,255,255,0.90);  /* brighter tone */
  margin: 0;
}

/* ensure Element Plus header inherits vertical alignment */
::v-deep .el-collapse-item__header {
  align-items: center;
  padding: 12px 16px;
}

.thumb-wrap {
  display: flex;
  align-items: center;     /* vertical center thumbnail and title */
  gap: 12px;
  overflow: hidden;        /* prevent children from escaping */
}

/* project title: bright white, vertically centered, allow wrapping */
.project-title {
  flex: 1;                      /* take remaining space beside thumbnail */
  min-width: 0;                 /* <-- critical: allow flex child to shrink and wrap */
  color: #ffffff;               /* bright white */
  display: block;
  white-space: normal;          /* allow wrapping */
  overflow-wrap: anywhere;
  word-break: break-word;       /* break long words if needed */
  overflow: visible;
  font-weight: 400;
  font-size: 0.95rem;
  line-height: 1.3;
  padding-left: 10px;
  padding-right: 10px;
}

/* keep thumbnail sizing but ensure centered content and prevent shrinking */
.thumb {
  flex: 0 0 120px;     /* fixed box beside the title */
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
}
</style>